"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import ActionButtons from "@/components/shared/navbar/ActionButtons";
import { SearchBar, MARKETPLACE_SEARCH_PLACEHOLDER_TERMS } from "@/components/features/home/hero/components/SearchBar";
import { SearchDropdown } from "./listing/SearchDropdown";
import { useMarketplaceSearchSuggestions } from "./listing/useMarketplaceSearchSuggestions";
import { loginFlow } from "@/components/shared/modals";
import DynamicModal from "@/components/shared/modals/DynamicModal";
import { ModalFlow } from "@/types/components/modal";
import { useAuth } from "@/hooks/auth/useAuth";
import { FORM_TYPES, LOGIN_LEAD_TRIGGER_SOURCES } from "@/lib/constants";
import type { MarketplaceSearchResult } from "@/types/api/marketplace";

interface MarketplaceNavbarProps {
  searchSuggestions: MarketplaceSearchResult[];
  searchValue: string;
  onSearchChange: (query: string) => void;
  onSearchCommit: (query: string) => void;
  onSearchSuggestionSelect?: (result: MarketplaceSearchResult) => void;
  debouncedSearch?: string;
  isSearchFetching?: boolean;
}

export default function MarketplaceNavbar({
  searchSuggestions,
  searchValue,
  onSearchChange,
  onSearchCommit,
  onSearchSuggestionSelect,
  debouncedSearch = "",
  isSearchFetching = false,
}: MarketplaceNavbarProps) {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const searchAreaRef = useRef<HTMLDivElement>(null);
  const [modalFlow, setModalFlow] = useState<ModalFlow | null>(null);
  const [postLoginRedirect, setPostLoginRedirect] = useState<string | null>(
    null
  );

  const { isAuthenticated } = useAuth();

  const searchPanel = useMarketplaceSearchSuggestions({
    searchQuery: searchValue,
    debouncedSearch,
    suggestions: searchSuggestions,
    isFetching: isSearchFetching,
    isFocused: isInputFocused,
  });

  useEffect(() => {
    if (isAuthenticated && postLoginRedirect && !modalFlow) {
      window.location.href = postLoginRedirect;
      setPostLoginRedirect(null);
    }
  }, [isAuthenticated, postLoginRedirect, modalFlow]);

  const handleFocus = () => {
    setIsInputFocused(true);
  };

  const handleBlur = () => {
    setIsInputFocused(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  useEffect(() => {
    if (!isInputFocused) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        searchAreaRef.current &&
        !searchAreaRef.current.contains(event.target as Node)
      ) {
        setIsInputFocused(false);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isInputFocused]);

  const openModal = (flow: ModalFlow | null) => setModalFlow(flow);
  const closeModal = () => setModalFlow(null);

  const handleLoginClick = (redirectUrl?: string) => {
    if (redirectUrl) setPostLoginRedirect(redirectUrl);
    openModal(loginFlow());
  };

  return (
    <div>
      <link rel="preload" as="image" href="/icons/logo.svg" />

      <nav
        id="nav"
        className="marketplace-navbar w-full bg-white">
        <div className="app-container flex w-full flex-nowrap items-center gap-3 py-2 md:gap-6 md:pt-3 md:pb-4">
          <Link href="/" className="flex shrink-0">
            <Image
              src="/icons/logo.svg"
              alt="Travel LYKKE Logo"
              width={110}
              height={40}
              className="h-10 sm:h-12"
              priority
            />
          </Link>

          <div
            ref={searchAreaRef}
            className="relative hidden min-w-0 flex-1 md:block md:min-w-0 md:max-w-3xl">
            <SearchBar
              searchBarContainerClassName="!mx-0 !w-full !max-w-none shadow-lg !h-14"
              searchValue={searchValue}
              setSearchValue={onSearchChange}
              onSearchSubmit={onSearchCommit}
              onFocus={handleFocus}
              onBlur={handleBlur}
              iconSize={20}
              iconContainerClassName="h-11 w-11"
              placeholderTerms={MARKETPLACE_SEARCH_PLACEHOLDER_TERMS}
            />
            {searchPanel.showPanel && (
              <div className="absolute top-full left-0 z-50 mt-1 w-full pt-2">
                <SearchDropdown
                  status={searchPanel.status}
                  results={searchPanel.displayResults}
                  relatedResults={searchPanel.displayRelated}
                  searchQuery={searchPanel.activeQuery}
                  onResultClick={handleBlur}
                  onCommitSearch={(query) => {
                    onSearchCommit(query);
                    handleBlur();
                  }}
                  onSuggestionSelect={(result) => {
                    onSearchSuggestionSelect?.(result);
                    handleBlur();
                  }}
                  desktopTall
                  className="shadow-shadow-search-bar overflow-hidden rounded-3xl border border-gray-200"
                />
              </div>
            )}
          </div>

          <div className="ml-auto flex shrink-0 items-center justify-end gap-2">
            <ActionButtons
              onLoginClick={handleLoginClick}
              showSearchInNav={false}
              showMyBookings={false}
            />
          </div>
        </div>
      </nav>

      {modalFlow && (
        <DynamicModal
          flow={modalFlow}
          isOpen={!!modalFlow}
          onClose={closeModal}
          metadata={{
            form_name: FORM_TYPES.LOGIN_FORM,
            page_url: window.location.pathname,
            referring_callout:
              postLoginRedirect === "/ai-planner"
                ? LOGIN_LEAD_TRIGGER_SOURCES.PLAN_WITH_AI
                : LOGIN_LEAD_TRIGGER_SOURCES.LOGIN_BUTTON,
          }}
        />
      )}
    </div>
  );
}
